import React, { useCallback, useEffect, useState } from 'react'
import { useParams, useHistory } from 'react-router-dom'
import { Container, Card, CardBody, Row, Col, Button, Table } from 'reactstrap'
import CurrencyFormat from 'react-currency-format'
import Moment from 'react-moment'
import toastr from 'toastr'
import 'toastr/build/toastr.min.css'

import Breadcrumbs from '../../components/Global/Breadcrumbs'
import ApiService from '../../data/services/ApiService'
import {
    getStatusBadge,
    getTipoComprobanteInfo,
    buildEmisor,
    buildReceptor,
    buildConceptos,
    buildTotales,
    buildTimbre,
    buildCfdiRelacionados,
} from '../../helpers/facturaDetailHelper'

// ── Styles ───────────────────────────────────────────────────────────────────

const SECTION_TITLE = {
    fontSize: 11,
    fontWeight: 600,
    letterSpacing: '0.07em',
    textTransform: 'uppercase',
    color: 'var(--color-text-tertiary, #888)',
    marginBottom: 8,
    marginTop: 16,
    borderBottom: '0.5px solid var(--color-border-tertiary, #e9ecef)',
    paddingBottom: 4,
}

const FIELD_LABEL = {
    fontSize: 11,
    color: 'var(--color-text-secondary, #888)',
    marginBottom: 1,
}

const FIELD_VALUE = {
    fontSize: 13,
    color: 'var(--color-text-primary, #222)',
    marginBottom: 8,
    wordBreak: 'break-word',
}

// ── Skeleton ─────────────────────────────────────────────────────────────────

const SkeletonLine = ({ width = '100%', height = 14, style = {} }) => (
    <div
        style={{
            width,
            height,
            background: 'linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%)',
            backgroundSize: '200% 100%',
            animation: 'shimmer 1.4s infinite',
            borderRadius: 4,
            marginBottom: 8,
            ...style,
        }}
    />
)

const LoadingSkeleton = () => (
    <Card>
        <CardBody>
            <style>{`
                @keyframes shimmer {
                    0% { background-position: 200% 0; }
                    100% { background-position: -200% 0; }
                }
            `}</style>
            <SkeletonLine width="40%" height={22} />
            <SkeletonLine width="25%" height={16} style={{ marginBottom: 24 }} />
            <SkeletonLine width="60%" />
            <SkeletonLine width="50%" />
            <SkeletonLine width="70%" />
            <SkeletonLine width="55%" />
        </CardBody>
    </Card>
)

// ── Field helper ─────────────────────────────────────────────────────────────

const Field = ({ label, value }) => (
    <div>
        <div style={FIELD_LABEL}>{label}</div>
        <div style={FIELD_VALUE}>{value || '—'}</div>
    </div>
)

// ── CadenaExpandable ─────────────────────────────────────────────────────────

const CadenaExpandable = ({ value }) => {
    const [expanded, setExpanded] = useState(false)

    if (!value) return <span style={{ color: '#adb5bd' }}>—</span>

    return (
        <div>
            <div
                style={{
                    fontSize: 11,
                    fontFamily: 'monospace',
                    wordBreak: 'break-all',
                    color: 'var(--color-text-secondary, #555)',
                    maxHeight: expanded ? 'none' : 48,
                    overflow: 'hidden',
                }}
            >
                {value}
            </div>
            <button
                onClick={() => setExpanded((e) => !e)}
                style={{
                    background: 'none',
                    border: 'none',
                    padding: 0,
                    fontSize: 11,
                    color: '#378ADD',
                    cursor: 'pointer',
                    marginTop: 2,
                }}
            >
                {expanded ? 'Colapsar ↑' : 'Expandir ↓'}
            </button>
        </div>
    )
}

// ── Main component ────────────────────────────────────────────────────────────

const FacturaDetail = () => {
    const { id } = useParams()
    const history = useHistory()

    const [loading, setLoading] = useState(true)
    const [error, setError] = useState(null)
    const [factura, setFactura] = useState(null)
    const [emisor, setEmisor] = useState(null)
    const [receptor, setReceptor] = useState(null)
    const [conceptos, setConceptos] = useState([])
    const [totales, setTotales] = useState(null)
    const [timbre, setTimbre] = useState(null)
    const [cfdiRelacionados, setCfdiRelacionados] = useState([])

    const _load = useCallback(async () => {
        setLoading(true)
        setError(null)
        try {
            const response = await ApiService.getFactura(id)
            const { factura: f, cliente, empresa } = response.data.data

            setFactura(f)
            setEmisor(buildEmisor(empresa || {}, f))
            setReceptor(buildReceptor(cliente || {}, f))
            setConceptos(buildConceptos(f.lineItems || []))
            setTotales(buildTotales(f))
            setTimbre(buildTimbre(f))
            setCfdiRelacionados(buildCfdiRelacionados(f))
        } catch (err) {
            const msg = err.response?.data?.message || 'No pude cargar la factura.'
            setError(msg)
            toastr.error(msg)
        } finally {
            setLoading(false)
        }
    }, [id])

    useEffect(() => {
        _load()
    }, [_load])

    const _downloadInvoice = async (type) => {
        try {
            await ApiService.downloadInvoice(factura.uuid, type)
        } catch {
            toastr.error(`Error al descargar el ${type.toUpperCase()}.`)
        }
    }

    const _downloadAcuseCancelacion = async (type) => {
        try {
            await ApiService.downloadAcuseCancelacion(factura.uuid, type)
        } catch {
            toastr.error('Error al descargar el acuse de cancelación.')
        }
    }

    const _verificarFacturaSat = async () => {
        try {
            setLoading(true)
            const response = await ApiService.verificarFacturaSat(factura._id)

            if (response.data.code === 'CFDI_STILL_ACTIVE') {
                toastr.warning(response.data.message)
            } else {
                toastr.success(response.data.message)
            }

            await _load()
        } catch (err) {
            toastr.error(err.response?.data?.message || 'No pude actualizar el estado de la factura en el SAT.')
            setLoading(false)
        }
    }

    if (loading) {
        return (
            <Container fluid>
                <Breadcrumbs title="Facturación" breadcrumbItem="Detalle de Factura" parentLink="/facturas" />
                <LoadingSkeleton />
            </Container>
        )
    }

    if (error || !factura) {
        return (
            <Container fluid>
                <Breadcrumbs title="Facturación" breadcrumbItem="Detalle de Factura" parentLink="/facturas" />
                <Card>
                    <CardBody>
                        <p style={{ color: '#f46a6a', marginBottom: 16 }}>
                            {error || 'Factura no encontrada.'}
                        </p>
                        <Button color="secondary" size="sm" onClick={() => history.goBack()}>
                            ← Volver
                        </Button>
                    </CardBody>
                </Card>
            </Container>
        )
    }

    const badge = getStatusBadge(factura.status)
    const tipoInfo = getTipoComprobanteInfo(factura.tipoComprobante)
    const isDownloadable = ['timbrada', 'cerrada', 'cancelada', 'cancelación rechazada'].includes(factura.status)

    return (
        <Container fluid>
            <Breadcrumbs
                title="Facturación"
                breadcrumbItem={`${tipoInfo.label} ${factura.serie ? factura.serie + '-' : ''}${factura.folio || '—'}`}
                parentLink="/facturas"
            />

            <Card>
                <CardBody>
                    {/* ── Header ─────────────────────────────────────────── */}
                    <Row className="align-items-center mb-3">
                        <Col sm={8}>
                            <h4 style={{ margin: 0, fontWeight: 600 }}>
                                {tipoInfo.label}{' '}
                                {factura.serie ? `${factura.serie}-` : ''}
                                {factura.folio || '—'}
                                {tipoInfo.badgeLabel && (
                                    <span
                                        className={`badge ml-2${tipoInfo.badgeClass ? ` ${tipoInfo.badgeClass}` : ''}`}
                                        style={!tipoInfo.badgeClass ? { fontSize: '0.78rem', fontWeight: 600, padding: '3px 11px' } : undefined}
                                    >
                                        {tipoInfo.badgeLabel}
                                    </span>
                                )}
                            </h4>
                            {factura.fechaTimbrado && (
                                <div style={{ fontSize: 12, color: 'var(--color-text-secondary, #888)', marginTop: 4 }}>
                                    Timbrada el{' '}
                                    <Moment format="DD/MM/YYYY HH:mm">{factura.fechaTimbrado}</Moment>
                                </div>
                            )}
                        </Col>
                        <Col sm={4} className="text-right">
                            <span
                                style={{
                                    background: badge.color,
                                    color: '#fff',
                                    borderRadius: 4,
                                    padding: '4px 12px',
                                    fontSize: 12,
                                    fontWeight: 600,
                                }}
                            >
                                {badge.label}
                            </span>
                        </Col>
                    </Row>

                    {/* ── Emisor ─────────────────────────────────────────── */}
                    <div style={SECTION_TITLE}>Emisor</div>
                    <Row>
                        <Col sm={6}>
                            <Field label="Nombre / Razón Social" value={emisor?.nombre} />
                        </Col>
                        <Col sm={3}>
                            <Field label="RFC" value={emisor?.rfc} />
                        </Col>
                        <Col sm={3}>
                            <Field label="Régimen Fiscal" value={emisor?.regimenFiscal} />
                        </Col>
                    </Row>
                    <Row>
                        <Col sm={3}>
                            <Field label="Lugar de Expedición" value={emisor?.lugarExpedicion} />
                        </Col>
                    </Row>

                    {/* ── Receptor ───────────────────────────────────────── */}
                    <div style={SECTION_TITLE}>Receptor</div>
                    <Row>
                        <Col sm={6}>
                            <Field label="Nombre / Razón Social" value={receptor?.nombre} />
                        </Col>
                        <Col sm={3}>
                            <Field label="RFC" value={receptor?.rfc} />
                        </Col>
                        <Col sm={3}>
                            <Field label="Domicilio Fiscal" value={receptor?.domicilioFiscal} />
                        </Col>
                    </Row>
                    <Row>
                        <Col sm={3}>
                            <Field label="Régimen Fiscal" value={receptor?.regimenFiscal} />
                        </Col>
                        <Col sm={3}>
                            <Field label="Uso CFDI" value={receptor?.usoCFDI} />
                        </Col>
                        {receptor?.residenciaFiscal && (
                            <Col sm={3}>
                                <Field label="Residencia Fiscal" value={receptor.residenciaFiscal} />
                            </Col>
                        )}
                    </Row>

                    {/* ── Conceptos ──────────────────────────────────────── */}
                    <div style={SECTION_TITLE}>Conceptos</div>
                    <div style={{ overflowX: 'auto' }}>
                        <Table size="sm" bordered style={{ fontSize: 12 }}>
                            <thead style={{ background: 'var(--color-background-secondary, #f8f9fa)' }}>
                                <tr>
                                    <th>Descripción</th>
                                    <th style={{ width: 80, textAlign: 'right' }}>Cantidad</th>
                                    <th style={{ width: 80 }}>Unidad</th>
                                    <th style={{ width: 120, textAlign: 'right' }}>Precio Unitario</th>
                                    <th style={{ width: 120, textAlign: 'right' }}>Importe</th>
                                </tr>
                            </thead>
                            <tbody>
                                {conceptos.map((c, i) => (
                                    <tr key={i}>
                                        <td>
                                            <div>{c.descripcion}</div>
                                            {c.claveProdServ && (
                                                <div style={{ fontSize: 10, color: '#adb5bd' }}>
                                                    Clave: {c.claveProdServ}
                                                </div>
                                            )}
                                        </td>
                                        <td style={{ textAlign: 'right' }}>{c.cantidad}</td>
                                        <td>{c.claveUnidad}</td>
                                        <td style={{ textAlign: 'right' }}>
                                            <CurrencyFormat
                                                value={c.valorUnitario}
                                                displayType="text"
                                                thousandSeparator
                                                prefix="$"
                                                decimalScale={2}
                                                fixedDecimalScale
                                            />
                                        </td>
                                        <td style={{ textAlign: 'right' }}>
                                            <CurrencyFormat
                                                value={c.importe}
                                                displayType="text"
                                                thousandSeparator
                                                prefix="$"
                                                decimalScale={2}
                                                fixedDecimalScale
                                            />
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </Table>
                    </div>

                    {/* ── Totales ────────────────────────────────────────── */}
                    <div style={SECTION_TITLE}>Totales</div>
                    {totales && (
                        <Row>
                            <Col sm={{ size: 4, offset: 8 }}>
                                <Table size="sm" borderless style={{ fontSize: 13 }}>
                                    <tbody>
                                        <tr>
                                            <td>Subtotal</td>
                                            <td className="text-right">
                                                <CurrencyFormat value={totales.subtotal} displayType="text" thousandSeparator prefix="$" decimalScale={2} fixedDecimalScale />
                                            </td>
                                        </tr>
                                        {totales.descuento !== null && (
                                            <tr>
                                                <td>Descuento</td>
                                                <td className="text-right">
                                                    <CurrencyFormat value={totales.descuento} displayType="text" thousandSeparator prefix="-$" decimalScale={2} fixedDecimalScale />
                                                </td>
                                            </tr>
                                        )}
                                        {totales.iva !== null && (
                                            <tr>
                                                <td>IVA</td>
                                                <td className="text-right">
                                                    <CurrencyFormat value={totales.iva} displayType="text" thousandSeparator prefix="$" decimalScale={2} fixedDecimalScale />
                                                </td>
                                            </tr>
                                        )}
                                        {totales.retenciones !== null && (
                                            <tr>
                                                <td>Retenciones</td>
                                                <td className="text-right">
                                                    <CurrencyFormat value={totales.retenciones} displayType="text" thousandSeparator prefix="-$" decimalScale={2} fixedDecimalScale />
                                                </td>
                                            </tr>
                                        )}
                                        <tr style={{ borderTop: '1px solid #dee2e6', fontWeight: 600 }}>
                                            <td>Total</td>
                                            <td className="text-right">
                                                <CurrencyFormat value={totales.total} displayType="text" thousandSeparator prefix="$" decimalScale={2} fixedDecimalScale />
                                            </td>
                                        </tr>
                                        <tr>
                                            <td style={{ fontSize: 11, color: '#adb5bd' }}>Moneda</td>
                                            <td className="text-right" style={{ fontSize: 11, color: '#adb5bd' }}>{totales.moneda}</td>
                                        </tr>
                                        {totales.tipoCambio !== null && (
                                            <tr>
                                                <td style={{ fontSize: 11, color: '#adb5bd' }}>Tipo de Cambio</td>
                                                <td className="text-right" style={{ fontSize: 11, color: '#adb5bd' }}>{totales.tipoCambio}</td>
                                            </tr>
                                        )}
                                    </tbody>
                                </Table>
                            </Col>
                        </Row>
                    )}

                    {/* ── Timbre Fiscal Digital ──────────────────────────── */}
                    {timbre && (
                        <>
                            <div style={SECTION_TITLE}>Timbre Fiscal Digital</div>
                            <Row>
                                <Col sm={12}>
                                    <Field label="UUID (Folio Fiscal)" value={timbre.uuid?.toUpperCase()} />
                                </Col>
                            </Row>
                            <Row>
                                <Col sm={4}>
                                    {timbre.fechaTimbrado && (
                                        <Field
                                            label="Fecha de Timbrado"
                                            value={
                                                <Moment format="DD/MM/YYYY HH:mm:ss">{timbre.fechaTimbrado}</Moment>
                                            }
                                        />
                                    )}
                                </Col>
                            </Row>
                            {timbre.cadenaOriginalTFD && (
                                <div style={{ marginBottom: 12 }}>
                                    <div style={FIELD_LABEL}>Cadena Original del TFD</div>
                                    <CadenaExpandable value={timbre.cadenaOriginalTFD} />
                                </div>
                            )}
                        </>
                    )}

                    {/* ── CFDI Relacionados ──────────────────────────────── */}
                    {cfdiRelacionados.length > 0 && (
                        <>
                            <div style={SECTION_TITLE}>CFDI Relacionados</div>
                            <Table size="sm" bordered style={{ fontSize: 12 }}>
                                <thead style={{ background: 'var(--color-background-secondary, #f8f9fa)' }}>
                                    <tr>
                                        <th>Tipo de Relación</th>
                                        <th>UUID</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {cfdiRelacionados.map((rel, i) => (
                                        <tr key={i}>
                                            <td>{rel.tipoRelacion || '—'}</td>
                                            <td style={{ fontFamily: 'monospace', fontSize: 11 }}>
                                                {rel.uuid || '—'}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </Table>
                        </>
                    )}

                    {/* ── Actions ────────────────────────────────────────── */}
                    <div style={{ display: 'flex', gap: 8, marginTop: 24, flexWrap: 'wrap' }}>
                        {isDownloadable && (
                            <>
                                <Button
                                    color="success"
                                    size="sm"
                                    onClick={() => _downloadInvoice('pdf')}
                                >
                                    <i className="bx bxs-file-pdf mr-1"></i>
                                    Descargar PDF
                                </Button>
                                <Button
                                    color="outline-success"
                                    size="sm"
                                    onClick={() => _downloadInvoice('xml')}
                                >
                                    <i className="bx bxs-file-blank mr-1"></i>
                                    Descargar XML
                                </Button>
                            </>
                        )}
                        {['cancelada', 'cancelación en proceso'].includes(factura.status) && (
                            <>
                                {factura.status === 'cancelación en proceso' && (
                                    <Button
                                        color="warning"
                                        size="sm"
                                        onClick={_verificarFacturaSat}
                                    >
                                        <i className="mdi mdi-refresh mr-1"></i>
                                        Actualizar estado en SAT
                                    </Button>
                                )}
                                <Button
                                    color="info"
                                    size="sm"
                                    onClick={() => _downloadAcuseCancelacion('pdf')}
                                >
                                    <i className="bx bxs-file-pdf mr-1"></i>
                                    Acuse PDF
                                </Button>
                                <Button
                                    color="outline-info"
                                    size="sm"
                                    onClick={() => _downloadAcuseCancelacion('xml')}
                                >
                                    <i className="bx bxs-file-blank mr-1"></i>
                                    Acuse XML
                                </Button>
                            </>
                        )}
                        <Button color="secondary" size="sm" onClick={() => history.goBack()}>
                            ← Volver
                        </Button>
                    </div>
                </CardBody>
            </Card>
        </Container>
    )
}

export default FacturaDetail
